<script setup lang="ts">
import { nextTick, ref } from 'vue';
import preencherInformacoesNecessariasDistribuicaoPastaQuery from '~/graphql/preencherInformacoesNecessariasDistribuicaoPasta.graphql';
import usuarioSelect from '~/graphql/usuarioSelect.graphql';
import { isNilOrEmpty, isNullOrEmpty } from '~/utils';
import agenciaSelect from '~/graphql/agenciaSelect.graphql';
import pessoaSelect from '~/graphql/pessoaSelect.graphql';
import carteiraSelect from '~/graphql/carteiraSelect.graphql';
import agenciaEmailSelect from '~/graphql/agenciaEmailSelect.graphql';
import { useDrawerStore } from '~/stores/drawer';

interface Props
{
  compromisso: any
}

const props = defineProps<Props>();

const emit = defineEmits(['close']);

const drawerStore = useDrawerStore();

const agenciaEmailsRef = ref<any>(null);

async function onAgenciaChange()
{
  await nextTick(async () =>
  {
    await agenciaEmailsRef.value.reset();
    await agenciaEmailsRef.value.clearItems();
  });
}

function abrirNovoAgenciaEmailDrawer(model: any)
{
  drawerStore.push2({
    componentName: 'NovoAgenciaEmailDrawer',
    params: {
      agenciaCodigo: model.agencia.value,
    },
    onClose: async () =>
    {
      await agenciaEmailsRef.value.reloadAsync();
    },
  });
}
</script>

<template>
  <GqlView
    :query="preencherInformacoesNecessariasDistribuicaoPastaQuery"
    :variables="{
      where: {
        codigo: {
          eq: props.compromisso.codigo,
        },
      },
    }"
  >
    <template #success="{ data } : { data: any }">
      <CompromissoJobCommandForm
        command="PreencherInformacoesNecessariasDistribuicaoPastaCommand"
        :show-upload-files="false"
        :initial-model="{
          compromissoCodigos: props.compromisso ? [props.compromisso.codigo] : null,
          conta: data?.model.fluxo.pasta.conta,
          numeroCnj: data?.model.fluxo.pasta.numeroCnj,
          agencia: data?.model.fluxo.pasta.agencia2 ? {
            value: data?.model.fluxo.pasta.agencia2.codigo,
            label: data?.model.fluxo.pasta.agencia2.label,
          } : null,
          contrarioPrincipal: data?.model.fluxo.pasta.contrarioPrincipal ? {
            value: data?.model.fluxo.pasta.contrarioPrincipal.codigo,
            label: data?.model.fluxo.pasta.contrarioPrincipal.nome,
          } : null,
          responsavelNegocial: data?.model.fluxo.pasta.responsavelNegocial ? {
            value: data?.model.fluxo.pasta.responsavelNegocial.codigo,
            label: data?.model.fluxo.pasta.responsavelNegocial.nome,
          } : null,
          responsavelProcessual: data?.model.fluxo.pasta.responsavelProcessual ? {
            value: data?.model.fluxo.pasta.responsavelProcessual.codigo,
            label: data?.model.fluxo.pasta.responsavelProcessual.nome,
          } : null,
          gerenteNegocialResponsavel: data?.model.fluxo.pasta.gerenteNegocialResponsavel ? {
            value: data?.model.fluxo.pasta.gerenteNegocialResponsavel.codigo,
            label: data?.model.fluxo.pasta.gerenteNegocialResponsavel.nome,
          } : null,
          carteira: data?.model.fluxo.pasta.carteiraContratoVigente?.carteira ? {
            value: data?.model.fluxo.pasta.carteiraContratoVigente.carteira.codigo,
            label: data?.model.fluxo.pasta.carteiraContratoVigente.carteira.label,
          } : null,
          contrato: data?.model.fluxo.pasta.carteiraContratoVigente
            ? data.model.fluxo.pasta.carteiraContratoVigente.contrato
            : null,
        }"
        @close="() => emit('close')"
      >
        <template #form="{ model }">
          <a-form-item
            label="Agência"
            name="agencia"
            :rules="{
              message: 'Agência é obrigatória',
              required: true,
              trigger: 'change',
            }"
          >
            <GqlSelect
              v-model:value="model.agencia"
              :query="agenciaSelect"
              placeholder="Selecione a agência"
              :allow-clear="true"
              @change="onAgenciaChange"
            />
          </a-form-item>
          <a-form-item
            label="Emails da agência que receberão a notificação de distribuição"
            name="agenciaEmails"
            :rules="{
              message: 'Email da agência é obrigatório',
              required: true,
              trigger: 'change',
            }"
          >
            <a-row :gutter="8">
              <a-col flex="auto">
                <GqlSelect
                  ref="agenciaEmailsRef"
                  v-model:value="model.agenciaEmails"
                  :query="agenciaEmailSelect"
                  :allow-clear="true"
                  :variables="{
                    where: {
                      agenciaCodigo: {
                        in: !isNilOrEmpty(model.agencia)
                          ? [model.agencia.value]
                          : [],
                      },
                    },
                  }"
                  :disabled="isNilOrEmpty(model.agencia)"
                  placeholder="Seleciona um ou mais emails da agência"
                  mode="multiple"
                />
              </a-col>
              <a-col flex="70px">
                <a-button
                  style="width: 70px"
                  :disabled="isNilOrEmpty(model.agencia)"
                  @click="() => abrirNovoAgenciaEmailDrawer(model)"
                >
                  Novo
                </a-button>
              </a-col>
            </a-row>
          </a-form-item>
          <a-form-item
            label="Conta"
            name="conta"
            :rules="{
              message: 'Conta é obrigatória',
              required: true,
              trigger: 'change',
            }"
          >
            <a-input v-model:value="model.conta" />
          </a-form-item>
          <a-form-item
            name="carteira"
            label="Carteira"
            :rules="{
              message: 'Carteira é obrigatória',
              required: true,
              trigger: 'change',
            }"
          >
            <GqlSelect
              v-model:value="model.carteira"
              :query="carteiraSelect"
              placeholder="Selecione a carteira"
              :allow-clear="true"
            />
          </a-form-item>
          <a-form-item
            label="Contrato"
            name="contrato"
            :rules="{
              message: 'Contrato é obrigatório',
              required: true,
              trigger: 'change',
            }"
          >
            <a-input v-model:value="model.contrato" />
          </a-form-item>
          <a-form-item
            name="contrarioPrincipal"
            label="Contrário principal"
            :rules="{
              message: 'Contrário principal é obrigatório',
              required: true,
              trigger: 'change',
            }"
          >
            <GqlSelect
              v-model:value="model.contrarioPrincipal"
              :query="pessoaSelect"
              placeholder="Selecione o contrário"
              :allow-clear="true"
            />
          </a-form-item>
          <a-form-item
            name="responsavelNegocial"
            label="Responsável negocial"
            :rules="{
              message: 'Responsável negocial é obrigatório',
              required: true,
              trigger: 'change',
            }"
          >
            <GqlSelect
              v-model:value="model.responsavelNegocial"
              :query="usuarioSelect"
              :variables="{
                where: {
                  ativo: {
                    eq: true,
                  },
                },
                order: [
                  {
                    nome: 'ASC',
                  },
                ],
              }"
              placeholder="Selecione o responsável"
              :allow-clear="true"
            />
          </a-form-item>
          <a-form-item
            name="gerenteNegocialResponsavel"
            label="Gerente negocial responsável"
            :rules="{
              message: 'Gerente negocial responsável é obrigatório',
              required: true,
              trigger: 'change',
            }"
          >
            <GqlSelect
              v-model:value="model.gerenteNegocialResponsavel"
              :query="usuarioSelect"
              :variables="{
                where: {
                  ativo: {
                    eq: true,
                  },
                },
                order: [
                  {
                    nome: 'ASC',
                  },
                ],
              }"
              placeholder="Selecione o gerente"
              :allow-clear="true"
            />
          </a-form-item>
          <a-form-item
            name="responsavelProcessual"
            label="Responsável processual"
            :rules="!isNullOrEmpty(model.numeroCnj) ? {
              message: 'Responsável processual é obrigatório',
              required: true,
              trigger: 'change',
            } : undefined"
          >
            <GqlSelect
              v-model:value="model.responsavelProcessual"
              :query="usuarioSelect"
              :variables="{
                where: {
                  ativo: {
                    eq: true,
                  },
                },
                order: [
                  {
                    nome: 'ASC',
                  },
                ],
              }"
              placeholder="Selecione o responsável"
              :allow-clear="true"
            />
          </a-form-item>
        </template>
      </CompromissoJobCommandForm>
    </template>
  </GqlView>
</template>
